import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import { API_BASE_URL, API_ENDPOINTS } from '../constants';
import '../styles/Auth.css';
import { useTranslation } from 'react-i18next';

const API_URL = API_BASE_URL;

const Signup = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('signup'); // 'signup', 'verification'
  const [otp, setOtp] = useState('');
  const navigate = useNavigate();
  const storedPasswordRef = useRef(''); // Store password for verification step
  
  // Generate a random 6-digit OTP
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };
  
  const sendVerificationEmail = async (currentEmail, currentPassword) => {
    // Generate new OTP
    const newOtp = generateOTP();
    
    // Store OTP in local storage with 15-minute expiry
    const now = new Date();
    const expiryTime = now.getTime() + (15 * 60 * 1000); // 15 minutes
    
    localStorage.setItem(`ampyfin_otp_${currentEmail}`, JSON.stringify({
      otp: newOtp,
      expiry: expiryTime
    }));
    
    try {
      // Send email with OTP
      await emailjs.send(
        "service_1dds2uc",
        "template_4ngo4st",
        {
          passcode: newOtp,
          email: currentEmail // Use the email passed to the function
        },
        "gSvPoWeQ3JqzuuqSG"
      );
      storedPasswordRef.current = currentPassword; // Store password
      return true;
    } catch (error) {
      console.error('Failed to send verification email:', error);
      setError(t('signup.errors.verificationEmailFailed'));
      return false;
    }
  };

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('signup.errors.passwordsDoNotMatch'));
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t('signup.errors.passwordLength'));
      return;
    }

    setLoading(true);

    try {
      // 1. Check if email exists using the new checkOnly flag
      await axios.post(`${API_URL}${API_ENDPOINTS.SIGNUP}`, {
        email,
        checkOnly: true
      });

      // 2. If email doesn't exist (no error thrown), send verification email
      const emailSent = await sendVerificationEmail(email, password);
      if (emailSent) {
        setStep('verification');
      }
    } catch (err) {
      console.error('Error during initial signup step:', err);
      if (err.response && err.response.data && err.response.data.detail) {
         if (err.response.data.detail.toLowerCase().includes('email already registered')) {
           setError(t('signup.errors.emailAlreadyRegistered'));
         } else {
           setError(`${t('signup.errors.generic')}: ${err.response.data.detail}`);
         }
      } else {
        setError(t('signup.errors.checkingEmail'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    
    // Get OTP data from localStorage
    const otpKey = `ampyfin_otp_${email}`;
    const storedOtpData = localStorage.getItem(otpKey);
    
    if (!storedOtpData) {
      setError(t('signup.errors.verificationExpired'));
      return;
    }
    
    try {
      const otpData = JSON.parse(storedOtpData);
      const now = new Date().getTime();
      
      // Check if OTP has expired
      if (otpData.expiry < now) {
        localStorage.removeItem(otpKey);
        setError(t('signup.errors.verificationExpired'));
        return;
      }
      
      // Check if entered OTP matches
      if (otp !== otpData.otp) {
        setError(t('signup.errors.invalidVerificationCode'));
        return;
      }
      
      setLoading(true);
      
      // 1. OTP is correct - register user with the stored password
      try {
        const response = await axios.post(`${API_URL}${API_ENDPOINTS.SIGNUP}`, {
          email,
          password: storedPasswordRef.current, // Use the stored password
          checkOnly: false // Ensure we are doing the full signup now
        });
        
        // 2. Signup successful - store token and redirect to dashboard
        localStorage.removeItem(otpKey); // Clean up OTP data
        localStorage.setItem('ampyfin_token', response.data.access_token);
        
        // 3. Update authentication state
        if (typeof setIsAuthenticated === 'function') {
          setIsAuthenticated(true);
        }
        
        // 4. Redirect directly to dashboard
        navigate('/dashboard');
      } catch (err) {
        console.error('Final Signup error:', err);
        if (err.response && err.response.data && err.response.data.detail) {
           setError(`${t('signup.errors.failedToCreate')}: ${err.response.data.detail}`);
        } else {
           setError(t('signup.errors.failedToCreate'));
        }
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(t('signup.errors.verificationUnexpected'));
    } finally {
      setLoading(false);
    }
  };
  
  const resendOTP = async () => {
    setLoading(true);
    // Resend using the email and password from the state/ref
    await sendVerificationEmail(email, storedPasswordRef.current);
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('signup.title')}</h2>
        <p className="auth-subtitle">{t('signup.subtitle')}</p>
        
        {error && <div className="auth-error">{error}</div>}
        
        {step === 'signup' ? (
          <form onSubmit={handleInitialSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">{t('signup.form.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t('signup.form.emailPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">{t('signup.form.password')}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={t('signup.form.passwordPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('signup.form.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder={t('signup.form.confirmPasswordPlaceholder')}
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? t('signup.form.checking') : t('signup.form.continue')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="auth-form">
            <p className="verification-message">
              {t('signup.verification.sent', { email: <strong>{email}</strong> })}
            </p>
            
            <div className="form-group">
              <label htmlFor="otp">{t('signup.verification.code')}</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                placeholder={t('signup.verification.codePlaceholder')}
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? t('signup.verification.verifyingAndCreating') : t('signup.verification.verifyAndCreate')}
            </button>
            
            <p className="resend-link">
              {t('signup.verification.noCode')}{' '}
              <button 
                type="button" 
                className="text-button" 
                onClick={resendOTP}
                disabled={loading}
              >
                {t('signup.verification.resendCode')}
              </button>
            </p>
          </form>
        )}
        
        <div className="auth-footer">
          <p>{t('signup.haveAccount')} <Link to="/login" className="auth-link">{t('signup.login')}</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Signup; 